/**
 * @module trackingEvents
 * @description Theme-side click tracking. Publishes clicks to Shopify's analytics
 * bus; the Customer Events pixel forwards them to Meta.
 *
 *   [data-track-event] buttons        → cta_view_content_click → ViewContent
 *   #app-embed form submit button     → form_lead_submit       → Lead
 *
 * Load in theme.liquid (before </body>):
 *   <script type="module" src="{{ 'trackingEvents.js' | asset_url }}"></script>
 *
 * Mark every button that should fire ViewContent:
 *   <a href="..." data-track-event>ΔΗΜΙΟΥΡΓΗΣΤΕ ΤΗ ΔΙΚΗ ΣΑΣ</a>
 */

const ctaSelector = '[data-track-event]';
const formHostSelector = '#app-embed';
const formSubmitSelector = '[data-testid="btn-form-submit"]';

/**
 * Finds the first element in the event path matching a selector. Uses
 * composedPath() so clicks inside open shadow roots are visible too.
 *
 * @param {Event} event - The DOM event.
 * @param {string} selector - CSS selector to match.
 * @returns {Element|null} The matching element, or null.
 */
const findInEventPath = (event, selector) =>
    event.composedPath().find((node) => node instanceof Element && node.matches(selector)) ?? null;

/**
 * Publishes a ViewContent click for a marked CTA button.
 *
 * @param {Element} ctaElement - The clicked CTA element.
 * @returns {void}
 */
const publishViewContentClick = (ctaElement) => {
    window.Shopify.analytics.publish('cta_view_content_click', {
        label: ctaElement.innerHTML || ctaElement.textContent.trim(),
        pagePath: window.location.pathname,
    });
};

/**
 * Publishes a Lead for the app-embed form submit button.
 *
 * @param {Element} submitElement - The clicked submit button.
 * @returns {void}
 */
const publishFormLeadSubmit = (submitElement) => {
    window.Shopify.analytics.publish('form_lead_submit', {
        label: submitElement.textContent.trim(),
        pagePath: window.location.pathname,
    });
};

/**
 * Delegated click handler for ViewContent CTAs and the Lead form submit button.
 *
 * @param {MouseEvent} event - The click event.
 * @returns {void}
 */
const handleDocumentClick = (event) => {
    if (!window.Shopify?.analytics?.publish) return;

    const submitElement = findInEventPath(event, formSubmitSelector);
    if (submitElement?.getRootNode()?.host?.matches(formHostSelector)) {
        publishFormLeadSubmit(submitElement);
        return;
    }

    const ctaElement = findInEventPath(event, ctaSelector);
    if (ctaElement) publishViewContentClick(ctaElement);
};

if (!window.ctaTrackingInitialized) {
    window.ctaTrackingInitialized = true;
    document.addEventListener('click', handleDocumentClick, { capture: true });
}
